import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { promoActive } from "@/lib/promo";
import { usePromoCountdown, pad2 } from "@/lib/usePromoCountdown";

const HIDDEN_PREFIXES = ["/crm", "/estimate", "/login"];

/**
 * Slim sticky announcement bar (Grand Opening promo).
 * One config drives this bar, the homepage section and the quote engine.
 * Adds `has-promo-bar` on <body> so the fixed header shifts down (index.css).
 */
export default function PromoBar() {
  const [promo, setPromo] = useState(null);
  const { pathname } = useLocation();
  const cd = usePromoCountdown(promo);

  useEffect(() => {
    api.get("/pricing/config").then(({ data }) => setPromo(data.promo)).catch(() => {});
  }, []);

  const show = promoActive(promo) && !HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    document.body.classList.toggle("has-promo-bar", show);
    return () => document.body.classList.remove("has-promo-bar");
  }, [show]);

  if (!show) return null;

  return (
    <div data-testid="promo-bar"
      className="fixed top-0 inset-x-0 z-[80] h-12 bg-[#0a0a0c]/95 backdrop-blur border-b border-[#1F5EFF]/30">
      <div className="h-full max-w-7xl mx-auto px-4 flex items-center justify-between gap-3">
        <div className="hidden sm:flex items-center min-w-0">
          <span className="font-mono-alt text-xs tracking-[0.18em] text-white/90 truncate">
            🔥 {promo.bar_text}
          </span>
        </div>
        {cd && (
          <span className="hidden md:inline-flex flex-shrink-0 items-center gap-2 font-mono-alt text-[11px] tracking-widest text-emerald-300" data-testid="promo-bar-countdown">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            ENDS IN {cd.d}D {pad2(cd.h)}H {pad2(cd.m)}M {pad2(cd.s)}S
          </span>
        )}
        <Link to="/book" data-testid="promo-bar-cta"
          className="hidden sm:inline-flex flex-shrink-0 items-center px-5 py-1.5 rounded-full bg-[#1F5EFF] text-white font-mono-alt text-[11px] tracking-widest hover:bg-white hover:text-black transition-colors">
          {promo.bar_cta}
        </Link>
        <Link to="/book" data-testid="promo-bar-cta-mobile"
          className="sm:hidden flex-1 text-center font-mono-alt text-[11px] tracking-[0.14em] text-white/90 py-1">
          GRAND OPENING • UP TO {promo.percent}% OFF{cd ? ` • ENDS ${cd.d}D ${pad2(cd.h)}H` : ""} • {promo.bar_cta}
        </Link>
      </div>
    </div>
  );
}
